/**
 * Plaintext-Cache telemetry.
 *
 * Counts hit/miss/write/evict events for the IDB plaintextCache store
 * and flushes a snapshot to POST /debug/cache-stats every 5 min, plus
 * on visibility-hidden / pagehide. Server logs via pino (no DB write).
 *
 * Fire-and-forget: never throws into the decrypt path.
 */
import { api } from "./api";

const FLUSH_INTERVAL_MS = 5 * 60 * 1000;

let hit = 0;
let miss = 0;
let write = 0;
let evict = 0;
let windowStart = Date.now();
let flushTimer: ReturnType<typeof setInterval> | null = null;
let lifecycleBound = false;
let deviceId: string | null = null;

export function setCacheTelemetryDeviceId(id: string | null): void {
  deviceId = id;
}

export function recordCacheHit(): void { hit++; }
export function recordCacheMiss(): void { miss++; }
export function recordCacheWrite(): void { write++; }
export function recordCacheEvict(n = 1): void { evict += n; }

function snapshot(): { hit: number; miss: number; write: number; evict: number; windowMs: number } {
  const now = Date.now();
  const snap = { hit, miss, write, evict, windowMs: now - windowStart };
  hit = 0; miss = 0; write = 0; evict = 0;
  windowStart = now;
  return snap;
}

async function flush(): Promise<void> {
  const snap = snapshot();
  if (snap.hit + snap.miss + snap.write + snap.evict === 0) return;
  try {
    await api.post("/debug/cache-stats", { ...snap, deviceId: deviceId ?? undefined }, { raw: true });
  } catch {
    // Drop on the floor — telemetry must never break the app.
  }
}

export function startCacheTelemetry(): void {
  if (flushTimer) return;
  flushTimer = setInterval(() => { void flush(); }, FLUSH_INTERVAL_MS);
  if (!lifecycleBound && typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void flush();
    });
    window.addEventListener("pagehide", () => { void flush(); });
    lifecycleBound = true;
  }
}

export function stopCacheTelemetry(): void {
  if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
}
